"use client";

import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";

/**
 * The accepted search terms, as chips beside the input.
 *
 * Terms accumulate rather than replacing each other: the user picks a
 * suggestion, it becomes a chip, and the next suggestion narrows what is left.
 * That's the legacy interaction, and it's why this is a list rather than a
 * string.
 *
 * It lives in the URL, unlike the legacy Redux `dataset.search.masks`, so a
 * shared link reproduces the search the sender was looking at — the same reason
 * the selection, the page and the camera are all parameters here.
 *
 * Separated by `|` rather than the default comma: a search term with a comma in
 * it is ordinary ("Paris, 12e"), one with a pipe is not.
 */
const parseAsMasks = parseAsArrayOf(parseAsString, "|").withDefault([]);

export function useSearchMasks() {
  const [masks, setMasks] = useQueryState("q", parseAsMasks);

  const update = (next: string[]) => void setMasks(next.length > 0 ? next : null);

  return {
    masks,

    /**
     * The single string the items API searches on. Terms are joined by spaces,
     * which `websearch_to_tsquery` reads as "all of these" — the same AND the
     * legacy app got from one `match` clause per term.
     */
    searchText: masks.length > 0 ? masks.join(" ") : undefined,

    add(mask: string) {
      const trimmed = mask.trim();
      if (!trimmed || masks.includes(trimmed)) return;
      update([...masks, trimmed]);
    },

    remove(mask: string) {
      update(masks.filter((existing) => existing !== mask));
    },

    clear() {
      void setMasks(null);
    },
  };
}
